// Derived PrepCook stage (README §6.7, DECISIONS §E.7).
//
// cookpit v3.2 declares two distinct ingredient surfaces:
//   - cookpit.ingredients[]              — the RESOURCE / larder list
//     (what to own, with quantities). CookChow's gather checklist.
//   - cookpit.prerequisites.ingredients[] — the PREP STEPS for the live
//     cook (cut, season, chop…), each ref-linked to the resource it
//     transforms via ingredientRefs / utensilRefs / sundryRefs.
//
// When a file declares NO timed prep phase (no preCook, no prepCook) but
// DOES carry prerequisites prep, that prep is otherwise invisible during
// the cook. This module derives a PrepCook checklist stage from the
// prerequisites entries: an ordered list under one advisory budget timer.
//
// Budget: a declared schema.org `prepTime` seeds the timer (and is the
// only value that feeds serve-time anchoring — see Prereqs). When absent,
// an advisory default is used for display only; it never anchors anything.
//
// This is app-rendered UI, not cookpit tasks: no /complete-task calls, no
// classification, nothing enters the anchored schedule.

import type { Cookpit } from '@cookchow/shared';
import { durationToSeconds } from './helpers';
import { buildNoteLookup, type NoteEntry } from './notes';

type CookpitFile = Cookpit.CookpitCookingFileV32;
type Recipe = Readonly<CookpitFile>;
type PrereqItem = Cookpit.PrerequisiteItem;

/** Advisory fallback when a file states prep but no `prepTime` (30 min). */
export const DEFAULT_PREP_BUDGET_SECONDS = 30 * 60;

export interface PrepResource {
  text: string;
  /** Formatted quantity, e.g. "1.6 kg", when the resource carries one. */
  measure?: string;
}

export interface PrepStep {
  id: string;
  /** The prep instruction, e.g. "Braising steak trimmed and cut…". */
  text: string;
  /** Resolved ingredientRefs (name + measure). */
  resources: PrepResource[];
  /** Resolved utensilRefs (text), e.g. "Chopping board". */
  utensils: string[];
  /** Resolved sundryRefs (text), e.g. "Sea salt". */
  sundries: string[];
  /** Chef-hat help entries that reference this step's resources. */
  help: NoteEntry[];
}

export interface PrepBudget {
  seconds: number;
  /** True when seconds is the advisory fallback, not a declared prepTime. */
  approximate: boolean;
}

/**
 * Derive a PrepCook checklist when the file declares no timed prep phase
 * but carries prerequisites mise. The presence of prep — not of `prepTime`
 * — is the trigger (a file may state prep yet omit the optional prepTime).
 */
export function shouldDerivePrepChecklist(recipe: Recipe | null | undefined): boolean {
  if (!recipe) return false;
  const ck = recipe.cookpit;
  if (ck.preCook || ck.prepCook) return false;
  return (ck.prerequisites?.ingredients?.length ?? 0) > 0;
}

/** The budget timer value: declared `prepTime`, else the advisory default. */
export function computePrepBudget(recipe: Recipe): PrepBudget {
  const declared = durationToSeconds(recipe.prepTime);
  if (declared > 0) return { seconds: declared, approximate: false };
  return { seconds: DEFAULT_PREP_BUDGET_SECONDS, approximate: true };
}

/**
 * Seconds the derived PrepCook checklist occupies on the wall clock *before*
 * LiveCook — the declared `prepTime`, else the 30-minute advisory default.
 * 0 when the file declares timed prep or no prerequisites mise.
 *
 * The server anchor only knows the cookpit's timed phases, so a
 * derived-prep recipe (e.g. carbonara) anchors LiveCook at `serve −
 * liveCook` with no room for the checklist. This client-side lead pushes
 * the cook-start earlier so the Planner read-out, the T-10 alarm and the
 * read-ahead preview reflect the time the checklist actually needs. (The
 * immutable anchored schedule is unchanged — this is a client overlay.)
 */
export function derivedPrepLeadSeconds(recipe: Recipe | null | undefined): number {
  if (!recipe || !shouldDerivePrepChecklist(recipe)) return 0;
  return computePrepBudget(recipe).seconds;
}

function formatMeasure(ing: Cookpit.Ingredient): string | undefined {
  const q = ing.quantity;
  if (q == null) return ing.unit ?? undefined;
  const qStr = typeof q === 'number' ? String(q) : `${q.min}–${q.max}`;
  return ing.unit ? `${qStr} ${ing.unit}` : qStr;
}

// Index every resource by id so a prep step's refs resolve in O(1). Split
// sub-ids (which live inside a parent ingredient's splits[]) map to their
// parent so a ref to a split still resolves to a usable name.
function buildResourceIndex(recipe: Recipe): {
  ingredients: Map<string, Cookpit.Ingredient>;
  utensils: Map<string, string>;
  sundries: Map<string, string>;
} {
  const ingredients = new Map<string, Cookpit.Ingredient>();
  for (const ing of recipe.cookpit.ingredients ?? []) {
    ingredients.set(ing.id, ing);
    for (const sp of ing.splits ?? []) {
      if (sp && typeof sp.id === 'string' && !ingredients.has(sp.id)) ingredients.set(sp.id, ing);
    }
  }
  const utensils = new Map<string, string>();
  for (const u of recipe.cookpit.utensils ?? []) utensils.set(u.id, u.text);
  const sundries = new Map<string, string>();
  for (const s of recipe.cookpit.sundries ?? []) sundries.set(s.id, s.text);
  return { ingredients, utensils, sundries };
}

// Resolve a prep entry's chef-hat help: skills/notes whose resource refs
// intersect this entry's refs (the resource-ref direction — the entry and
// the help both point at the same ingredient/utensil/sundry), plus an
// explicit helpRef. De-duplicated, in lookup order.
function helpForEntry(
  entry: PrereqItem,
  recipe: Recipe,
  noteLookup: Map<string, NoteEntry>,
): NoteEntry[] {
  const myRefs = new Set<string>([
    ...(entry.ingredientRefs ?? []),
    ...(entry.utensilRefs ?? []),
    ...(entry.sundryRefs ?? []),
    ...(entry.equipmentRefs ?? []),
  ]);
  const ids = new Set<string>();
  if (entry.helpRef && noteLookup.has(entry.helpRef)) ids.add(entry.helpRef);
  const prereqs = recipe.cookpit.prerequisites;
  for (const bucket of [prereqs?.skills, prereqs?.notes]) {
    for (const item of bucket ?? []) {
      const refs = [
        ...(item.ingredientRefs ?? []),
        ...(item.utensilRefs ?? []),
        ...(item.sundryRefs ?? []),
        ...(item.equipmentRefs ?? []),
      ];
      if (refs.some((r) => myRefs.has(r))) ids.add(item.id);
    }
  }
  const out: NoteEntry[] = [];
  for (const id of ids) {
    const entryNote = noteLookup.get(id);
    if (entryNote) out.push(entryNote);
  }
  return out;
}

/**
 * Build the ordered prep checklist from the prerequisites entries: the mise
 * (ingredients) first, then setup (equipment, utensils). skills/notes/
 * hotspots are NOT rows — they surface as chef-hat help on the rows.
 */
export function buildPrepChecklist(recipe: Recipe): PrepStep[] {
  const prereqs = recipe.cookpit.prerequisites;
  if (!prereqs) return [];
  const index = buildResourceIndex(recipe);
  const noteLookup = buildNoteLookup(recipe);

  const ordered: PrereqItem[] = [
    ...(prereqs.ingredients ?? []),
    ...(prereqs.equipment ?? []),
    ...(prereqs.utensils ?? []),
  ];

  return ordered.map((entry) => {
    const resources: PrepResource[] = (entry.ingredientRefs ?? [])
      .map((id) => index.ingredients.get(id))
      .filter((x): x is Cookpit.Ingredient => !!x)
      .map((ing) => ({ text: ing.text, measure: formatMeasure(ing) }));
    const utensils = (entry.utensilRefs ?? [])
      .map((id) => index.utensils.get(id))
      .filter((x): x is string => !!x);
    const sundries = (entry.sundryRefs ?? [])
      .map((id) => index.sundries.get(id))
      .filter((x): x is string => !!x);
    return {
      id: entry.id,
      text: entry.text,
      resources,
      utensils,
      sundries,
      help: helpForEntry(entry, recipe, noteLookup),
    };
  });
}
